import { Button, Checkbox } from 'canoe';
import { ENDPOINTS, useActions } from 'hammock';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import LazyLoad from 'react-lazy-load';
import styled from 'styled-components';
import { colors, Footnote } from 'styleguide';

import { adjustImageSize } from 'commons/utils';
import { Trash } from 'styleguide/icons';

const Container = styled.div`
  border-radius: 4px;
  box-shadow: 0px 4px 4px rgba(67, 75, 104, 0.08);
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  margin-right: 8px;
  height: 200px;
  width: 220px;
  overflow: hidden;
  position: relative;

  &:hover > div {
    opacity: 1;
  }
`;

const ImageWrapper = styled<any>(LazyLoad)`
  display: flex;
  flex: 1;
  ${({ visible }) => visible && 'overflow: auto'};

  > img {
    display: block;
    margin: auto;
    max-height: 100%;
    max-width: 100%;
  }
`;

const ImageDescription = styled.div`
  align-items: center;
  background-color: ${colors('white')};
  display: flex;
  flex-direction: column;
  text-align: center;
  padding: 16px;
`;

const ImageHover = styled.div`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.3);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  opacity: 0;
  transition: opacity 0.3s;
  top: 0;
`;

const TrashButton = styled.div`
  cursor: pointer;
  position: absolute;
  top: 5px;
  right: 5px;
`;

const SelectButtonWrapper = styled.div`
  cursor: pointer;
  position: absolute;
  top: 5px;
  left: 5px;
`;

interface Props {
  toggleModal: () => void;
  image: { fileName: string; assetUri: { uri: string }; modifierAt: string; mediaId: number };
  parentRef: any;
  isAiMode: boolean;
  selectedImages: string[];
  changeSelectedImages: (images: string[]) => void;
  onRemoveImage: (id: number) => void;
  onSelectImage: (url: string, dimensions?: { width: number; height: number }) => void;
}

export const ImageContainer = ({
  image,
  toggleModal,
  parentRef,
  onSelectImage,
  onRemoveImage,
  isAiMode,
  selectedImages,
  changeSelectedImages,
}: Props) => {
  const loadedImage = useRef<HTMLImageElement>(null);
  const [visible, setVisible] = useState<boolean>();
  const actions = useActions(ENDPOINTS.media);

  const toggleImage = () => {
    if (selectedImages.includes(image.assetUri.uri)) {
      changeSelectedImages(selectedImages.filter((url) => url !== image.assetUri.uri));
    } else {
      changeSelectedImages([...selectedImages, image.assetUri.uri]);
    }
  };

  const onDelete = () => {
    onRemoveImage(image.mediaId);
    actions.delete({
      id: image.mediaId,
      onSuccessNotification: {
        message: 'Image Deleted',
      },
      invalidates: {
        [ENDPOINTS.images]: { query: { finder: 'byOrganizationId' } },
      },
    });
  };

  return (
    <Container key={image.assetUri.uri}>
      {isAiMode && selectedImages.includes(image.assetUri.uri) && (
        <SelectButtonWrapper>
          <Checkbox checked onChange={toggleImage} />
        </SelectButtonWrapper>
      )}
      <ImageWrapper
        height={200}
        offset={200}
        visible={visible}
        scrollContainer={parentRef}
        onContentVisible={() => setVisible(true)}
      >
        <img
          ref={loadedImage}
          src={adjustImageSize(image.assetUri.uri, 200)}
          alt={image.fileName}
        />
      </ImageWrapper>
      <ImageDescription>
        <Footnote weight="bold">{image.fileName}</Footnote>
        <Footnote color="neutral" shade={2}>
          {moment(image.modifierAt).fromNow()}
        </Footnote>
      </ImageDescription>
      <ImageHover>
        {isAiMode && (
          <SelectButtonWrapper>
            <Checkbox
              checked={selectedImages.includes(image.assetUri.uri)}
              onChange={toggleImage}
            />
          </SelectButtonWrapper>
        )}
        <TrashButton onClick={onDelete}>
          <Trash color="white" />
        </TrashButton>
        <Button
          disabled={!loadedImage}
          variant="secondary"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            toggleModal();
            onSelectImage(image.assetUri.uri);
          }}
        >
          Add Image
        </Button>
      </ImageHover>
    </Container>
  );
};
